import classnames from "classnames";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { IntlShape, defineMessages, useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { useCostCenters } from "shared/cbt/hooks";
import {
  addTravelerHotels,
  addTraveller,
  changeCostCenter,
  changeTravelReason,
  removeTravelerHotels,
  removeTraveller,
  resetGuestsAndTravellers,
} from "shared/data/actions/cbt";
import { getCbtToken } from "shared/data/customerTokens";
import { AppState } from "shared/data/reducers";
import { useHotelRoomManagement } from "shared/hooks/useHotelRoomManagement";
import { canAddCbtGuests } from "shared/lib/cbtGuests";
import { cutString } from "shared/lib/stringFormat";
import { formattedMessageParams } from "shared/messages";
import { AllowedTravelerType, HotelSettings } from "shared/types/hotels";

import Img from "@components/FileServer/Img";
import Counter from "@components/SearchForm/Counter";
import ReusableSelect from "@components/SearchForm/ReusableSelect";
import TravellersSelect from "@components/SearchForm/TravellersSelect";
import FormattedMessage from "@components/UI/FormattedMessage";
import CrossIcon from "@components/UI/Icons/Cross";
import SimpleSelect from "@components/UI/SimpleSelect";

interface IHotelPassengersSelectProps {
  hotelSearchSettings: HotelSettings | null | undefined;
  allowedTravelerTypes: AllowedTravelerType[];
  selectComponentPrefix: string;
  toggleShowProperties?: () => void;
  setErrorMessage: (message: string | null) => void;
}

const { pleaseSelect } = defineMessages({
  pleaseSelect: formattedMessageParams("General.pleaseSelect"),
});

const cbtSelectStyles = {
  menu: (base) => ({
    ...base,
    minWidth: "220px",
    left: "auto",
    right: 0,
  }),
  option: (base) => ({
    ...base,
    cursor: "pointer",
    whiteSpace: "nowrap",
  }),
};

const HotelPassengerSelect: React.FC<IHotelPassengersSelectProps> = ({
  hotelSearchSettings,
  allowedTravelerTypes,
  selectComponentPrefix,
  toggleShowProperties,
  setErrorMessage,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const cbtToken = getCbtToken();
  const isCbtEnabled = !!cbtToken;

  const [costCenterModalOpen, setCostCenterModalOpen] = useState<{
    open: boolean;

    chosen: string;
  }>({ open: false, chosen: "" });
  const [addPersonOpened, setAddPersonOpened] = useState<boolean>(false);

  const cbt = useSelector((state: AppState) => state.cbt);
  const customerUsername = useSelector((state: AppState) => state.user.email);

  const hotelRoomCbtAssignments = useSelector(
    (state: AppState) => state.cbt.passengersForm.hotelRoomAssignments
  );

  const costCenterModalOpenRef = useRef<HTMLDivElement>(null);

  const addPersonSelectRef = useRef<HTMLDivElement>(null);

  const hotelPassengerSelectRef = useRef<HTMLDivElement>(null);

  const { passengersForm, costCenters, travelReasons, corporate, users } = cbt;

  const {
    travelReason,
    costCenter,
    travellers: travellersOrigin,
  } = passengersForm;

  const travellers = travellersOrigin?.flatMap((t) => (t ? [t] : [])) || [];

  const selectedTravellersIds = new Set(travellers);
  const soloTraveler = !!corporate?.TravelArranger?.SoloTraveller;
  const canAddGuests = canAddCbtGuests(
    isCbtEnabled,
    corporate?.AllowGuests,
    soloTraveler
  );
  const canAddTravellers = !soloTraveler || travellers.length === 0;

  const { selectedCostCenter, allowedCostCenters } = useCostCenters({
    corporate,
    costCenter,
    costCenters,
    travellers,
    users,
    dispatch,
  });

  const hotelMaxRooms = parseInt(hotelSearchSettings?.RoomCount?.Max, 10) ?? 9;

  useLayoutEffect(() => {
    const close = (e) => {
      if (!addPersonSelectRef.current?.contains(e.target)) {
        setAddPersonOpened(false);
      }
      if (!costCenterModalOpenRef.current?.contains(e.target)) {
        setCostCenterModalOpen({
          choosen: selectedCostCenter
            ? selectedCostCenter.label
            : intl.formatMessage(pleaseSelect),
          open: false,
        });
      }
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [selectedCostCenter, intl]);

  useEffect(() => {
    // Only one searcher can be openned
    if (costCenterModalOpen.open) {
      if (addPersonOpened) {
        setCostCenterModalOpen({ ...costCenterModalOpen, open: false });
      }
    }
  }, [
    addPersonOpened,
    costCenterModalOpen,
    cbtToken,
    dispatch,
    users,
    corporate,
  ]);

  const {
    rooms,
    collapsedStates,
    maxTravelersError,
    maxRoomOccupancyError,
    maxRoomOccupancyErrorRoomIndex,
    handleAddPassenger,
    handleRemovePassenger,
    handleChildAgeChange,
    resetRoomsToDefault,
    addRoom,
    removeRoom,
    toggleRoomCollapse,
    isDefaultState,
    needMoreAdultsError,
  } = useHotelRoomManagement({
    hotelMaxRooms,
    hotelSearchSettings: hotelSearchSettings as any,
    setErrorMessage,
    isCbtEnabled,
    hotelRoomCbtAssignments: {
      hotelRoomAssignments: hotelRoomCbtAssignments,
    } as any,
  });

  const travelerTypesSettings =
    hotelSearchSettings?.RoomGuestLimits?.GuestLimit;

  const childAgeOptions = useMemo(() => {
    return generateChildAgeOptions(intl);
  }, [intl]);

  const handleToggleCostCenterModal = useCallback(() => {
    setCostCenterModalOpen((prevState) => ({
      ...prevState,
      open: !prevState.open,
    }));
  }, []);

  const handleToggleRoomCollapse = useCallback(
    (roomIndex: number) => {
      const isCurrentlyExpanded = !collapsedStates[roomIndex];
      if (isCurrentlyExpanded) {
        setAddPersonOpened(false);
      }
      toggleRoomCollapse(roomIndex);
    },
    [collapsedStates, toggleRoomCollapse]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const propertiesPanel = document.getElementById(
        `${selectComponentPrefix}-properties`
      );
      const toggleButton = document.getElementById(
        `${selectComponentPrefix}-div_toggle_properties`
      );
      const target = e.target as HTMLElement;

      if (!propertiesPanel || needMoreAdultsError || !toggleShowProperties) {
        return;
      }

      const excludedSelectors = [
        ".select-menu-outer",
        "#cbt-reusable-select",
        "#add-room-button",
        '[class*="travelersSelect"]',
        '[class*="passengers-options"]',
        ".remove-room-button",
        '[class*="react-select__menu"]',
        '[id*="react-select"]',
      ];

      const isInsideExcludedElement =
        excludedSelectors.some((selector) => target.closest(selector)) ||
        hotelPassengerSelectRef.current?.contains(target) ||
        propertiesPanel.contains(target) ||
        toggleButton?.contains(target) ||
        addPersonSelectRef.current?.contains(target);

      if (isInsideExcludedElement) {
        return;
      }

      toggleShowProperties();
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [toggleShowProperties, needMoreAdultsError, selectComponentPrefix]);

  if (
    !Array.isArray(travelerTypesSettings) ||
    travelerTypesSettings.length === 0
  ) {
    return <div>Loading hotel settings or no guest types defined...</div>;
  }

  const travelerNameMap = allowedTravelerTypes.reduce<Record<string, string>>(
    (acc, travelerType) => {
      acc[travelerType.Code] = travelerType.$t;
      return acc;
    },
    {}
  );

  const childSettings = travelerTypesSettings.find((tt) => tt.Code === "CHD");

  const renderRoomManagementButtons = () => (
    <div className="buttons-container">
      {rooms.length < hotelMaxRooms && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            addRoom();
          }}
          id="add-room-button"
          className="button button-secondary-color"
        >
          <FormattedMessage id="SearchForm.addRoom" />
        </button>
      )}
      <button
        type="button"
        onClick={() => {
          resetRoomsToDefault();
          dispatch(resetGuestsAndTravellers());
        }}
        id="reset-rooms-button"
        disabled={isDefaultState}
        className="button button-secondary-color"
      >
        {hotelMaxRooms > 1 ? (
          <FormattedMessage id="SearchForm.resetRooms" />
        ) : (
          <FormattedMessage id="SearchForm.resetRoom" />
        )}
      </button>
    </div>
  );

  return (
    <div ref={hotelPassengerSelectRef} className="hotel-passenger-select">
      {rooms.map((room, roomIndex) => {
        const totalPassengers =
          (Object.values(room).reduce(
            // @ts-ignore
            (sum, val) => sum + (typeof val === "number" ? val : 0),
            0
          ) as number) || 0;
        const isCollapsed = collapsedStates[roomIndex];
        const adults =
          !isCbtEnabled && totalPassengers === 0
            ? 1
            : (room.ADT as number) || 0;
        const children = (room.CHD as number) || 0;
        const ages = (room.childAges as number[]) || [];
        const guests = (room.guests as number) || 0;
        const travelers =
          Object.values(hotelRoomCbtAssignments?.[roomIndex] || {})?.length ||
          0;

        return (
          <div key={`room-${roomIndex}`} className="hotel-select-container">
            <div
              className={`room-summary-row ${isCollapsed ? "collapsed" : ""}`}
              onClick={() => handleToggleRoomCollapse(roomIndex)}
              id={`room-details-${roomIndex}${isCollapsed ? "-collapsed" : ""}`}
              role="button"
              tabIndex={0}
              aria-expanded={!isCollapsed}
              aria-controls={`room-details-${roomIndex}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleToggleRoomCollapse(roomIndex);
                }
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  removeRoom(roomIndex);
                }}
                aria-label={`Remove Room ${roomIndex + 1}`}
                className="remove-room-button"
              >
                <CrossIcon size="sm" color="white" />
              </button>
              <div className="room-summary-text">
                <span
                  className={
                    needMoreAdultsError ? "room-title-error" : "room-title"
                  }
                >
                  <FormattedMessage id="SearchForm.room" /> {roomIndex + 1}:
                </span>{" "}
                {isCbtEnabled && (
                  <>
                    <FormattedMessage
                      id={
                        soloTraveler ? "General.travellers" : "Passengers.title"
                      }
                    />
                    : {soloTraveler ? travelers : guests + travelers}
                  </>
                )}
                {!isCbtEnabled && (
                  <>
                    {`${travelerNameMap.ADT}: ${adults + guests + travelers}`}
                    {", "}
                    {`${travelerNameMap.CHD}: ${children}`}
                  </>
                )}
              </div>
              <span className="collapse-icon">
                <Img
                  src="/static/images/ico-arrow-down.svg"
                  id={`${selectComponentPrefix}-properties-arrow`}
                  style={{
                    padding: "2px",
                    marginLeft: "5px",
                    transition: "all .4s ease",
                    transform: isCollapsed ? "rotate(180deg)" : null,
                  }}
                />
              </span>
            </div>

            {!isCollapsed && (
              <div id={`room-details-${roomIndex}`} className="room-details">
                {isCbtEnabled && (
                  <div className="passengers-options-dialog__row">
                    <div>
                      <FormattedMessage id="General.travellers" />
                    </div>

                    <div
                      className="relative passengers-options-dialog__container"
                      ref={addPersonSelectRef}
                    >
                      <button
                        type="button"
                        id="cbt-add-traveler"
                        className="passengers-options-dialog__add-person-btn passenger-options-button-arrow-icon-indentation"
                        disabled={!canAddTravellers}
                        onClick={() => {
                          if (!canAddTravellers) {
                            return;
                          }
                          setAddPersonOpened((opened) => !opened);
                        }}
                      >
                        <FormattedMessage id="General.addPerson" />

                        <Img
                          src="/static/images/ico-arrow-down.svg"
                          alt="down arrow icon"
                          className={classnames(
                            {
                              "select-arrow-rotate-up": addPersonOpened,
                            },

                            "passenger-options-button-text-icon-indentation"
                          )}
                        />
                      </button>

                      {addPersonOpened && (
                        <div className="">
                          <TravellersSelect
                            className="passengers-options-dialog__travelers-select"
                            selectedTravellersIds={selectedTravellersIds}
                            isDisabled={!!maxTravelersError}
                            onChange={({ value }) => {
                              dispatch(addTraveller(value));

                              const roomAssignments = hotelRoomCbtAssignments;

                              for (let i = 0; i < rooms.length; i++) {
                                if (i === roomIndex) {
                                  const currentAssignments =
                                    roomAssignments[roomIndex] || {};

                                  const alreadyAssigned =
                                    currentAssignments &&
                                    Object.values(currentAssignments).includes(
                                      value
                                    );

                                  if (!alreadyAssigned) {
                                    let nextIndex = 0;

                                    while (
                                      Object.prototype.hasOwnProperty.call(
                                        currentAssignments,
                                        nextIndex
                                      )
                                    ) {
                                      nextIndex++;
                                    }

                                    roomAssignments[roomIndex] = {
                                      ...currentAssignments,
                                      [nextIndex]: value,
                                    };
                                  }
                                }
                              }

                              dispatch(addTravelerHotels(roomAssignments));
                            }}
                            tooltip={(option) => option.Email}
                            close={() => setAddPersonOpened(false)}
                            onlyCurrentUser={soloTraveler}
                            currentUserEmail={customerUsername}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div
                  className={classnames(
                    "passengers-options-dialog__travelers hotels-select",
                    {
                      "hotels-select--solo-traveler": soloTraveler,
                    }
                  )}
                >
                  {Object.entries(
                    hotelRoomCbtAssignments?.[roomIndex] || {}
                  ).map(
                    ([assignmentIdx, travelerId]) =>
                      travelerId &&
                      users[travelerId] && (
                        <div
                          key={assignmentIdx + travelerId}
                          className="passengers-options-dialog__traveler"
                        >
                          {users[travelerId]?.label}

                          {!soloTraveler && (
                            <button
                              className="passengers-options-dialog__travelers-remove-btn"
                              type="button"
                              onClick={() => {
                                dispatch(removeTraveller(travelerId));

                                dispatch(removeTravelerHotels(travelerId));
                              }}
                              title={intl.formatMessage(
                                formattedMessageParams("General.removeTraveler")
                              )}
                            >
                              {intl.formatMessage(
                                formattedMessageParams("General.removeTraveler")
                              )}
                            </button>
                          )}
                        </div>
                      )
                  )}
                </div>
                <div className={!isCbtEnabled && "passengers-config-row"}>
                  <div className="counters-row">
                    {canAddGuests && (
                      <div
                        key={`guest-counter-${roomIndex}`}
                        className="counter-item"
                      >
                        <span className="counter-label">
                          <FormattedMessage id="General.guests" />
                        </span>
                        <Counter
                          type="guests"
                          value={guests}
                          onPlusSign={() =>
                            handleAddPassenger(roomIndex, "guests")
                          }
                          onMinusSign={() =>
                            handleRemovePassenger(roomIndex, "guests")
                          }
                        />
                      </div>
                    )}
                    {!isCbtEnabled &&
                      travelerTypesSettings
                        .filter((tt) => tt.Code === "ADT" || tt.Code === "CHD")
                        .map((travelerType) => {
                          const travelerCode = travelerType.Code;
                          const currentValue =
                            (room[travelerCode] as number) ||
                            (travelerCode === "ADT" ? 1 : 0);
                          const displayName =
                            allowedTravelerTypes.find(
                              (att) => att.Code === travelerCode
                            )?.$t ?? travelerCode;

                          const travelerSettings = travelerTypesSettings.find(
                            (limit) => limit.Code === travelerCode
                          );
                          const minValue = travelerSettings?.Min
                            ? parseInt(travelerSettings.Min, 10)
                            : travelerCode === "ADT"
                            ? 1
                            : 0;

                          const maxOccupancy = Number(
                            travelerSettings?.Max ?? 9
                          );
                          const totalOccupants = adults + children;
                          const roomOccupancyMax = Number(
                            hotelSearchSettings?.RoomOccupancy?.Max ?? 9
                          );
                          // Disable plus if adding one more would exceed the room occupancy limit
                          const isMaxTotalOccupancy =
                            totalOccupants >= roomOccupancyMax;

                          const infantsAge = childSettings?.MaxAge ?? -1;
                          const nonInfantChildrenCount = ages.filter(
                            (age) => Number(age) <= Number(infantsAge)
                          ).length;

                          const isChildLimitReached =
                            travelerCode === "CHD" &&
                            childSettings?.LessOrEqualToAdults === "true" &&
                            nonInfantChildrenCount >= adults &&
                            adults > 0;

                          const chdWouldBeLessThanAdults =
                            travelerCode === "ADT" &&
                            childSettings?.LessOrEqualToAdults === "true" &&
                            (() => {
                              return currentValue === nonInfantChildrenCount;
                            })();

                          const disabledMinus =
                            currentValue <= minValue ||
                            chdWouldBeLessThanAdults;

                          const disabledPlus =
                            currentValue >= maxOccupancy ||
                            isMaxTotalOccupancy ||
                            isChildLimitReached;

                          return (
                            <div
                              key={`${travelerCode}-counter-${roomIndex}`}
                              className="counter-item"
                            >
                              <span className="counter-label">
                                {displayName}:
                              </span>
                              <Counter
                                type={travelerCode}
                                value={currentValue}
                                onPlusSign={() =>
                                  handleAddPassenger(roomIndex, travelerCode)
                                }
                                onMinusSign={() =>
                                  handleRemovePassenger(roomIndex, travelerCode)
                                }
                                disabledMinus={disabledMinus}
                                disabledPlus={disabledPlus}
                              />
                            </div>
                          );
                        })}
                  </div>

                  {children > 0 && (
                    <div className="child-ages-row">
                      <div className="child-age-label">
                        <FormattedMessage id="SearchForm.ageOfChildren" />
                      </div>
                      <div className="child-age-selects">
                        {Array.from({ length: Math.ceil(children / 4) }).map(
                          (_, rowIdx) => (
                            <div
                              key={`age-row-${roomIndex}-${rowIdx}`}
                              className="age-selector-row"
                            >
                              {Array.from({
                                length: Math.min(4, children - rowIdx * 4),
                              }).map((unused, idx) => {
                                const childIdx = rowIdx * 4 + idx;

                                const selectedAge =
                                  ages[childIdx]?.toString() || 1;

                                return (
                                  <div
                                    key={`age-group-${roomIndex}-${childIdx}`}
                                    className="age-selector-group"
                                  >
                                    <SimpleSelect
                                      key={`child-age-${roomIndex}-${childIdx}`}
                                      customClass={`${selectComponentPrefix}-child-age-select`}
                                      options={childAgeOptions}
                                      value={childAgeOptions.find((opt) => {
                                        return (
                                          opt.value === selectedAge.toString()
                                        );
                                      })}
                                      onChange={(selectedOption) => {
                                        if (selectedOption?.value) {
                                          handleChildAgeChange(
                                            roomIndex,
                                            childIdx,
                                            selectedOption.value
                                          );
                                        }
                                      }}
                                      isSearchable={false}
                                      testId={`child-age-${roomIndex}-${childIdx}`}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {isCbtEnabled && (
        <div className="room-management-buttons">
          {renderRoomManagementButtons()}
        </div>
      )}

      {isCbtEnabled && (
        <div className="cbt-properies-select-container">
          <div className="passengers-options-dialog__row">
            <div>
              <FormattedMessage id="General.costCentre" />
            </div>

            <div
              className="relative passengers-options-dialog__container"
              ref={costCenterModalOpenRef}
            >
              <button
                type="button"
                id="cbt-cost-center"
                className={`passengers-options-dialog__add-person-btn cost-center-font passenger-options-button-arrow-icon-indentation ${
                  selectedCostCenter ? "cost-center-selected" : ""
                }`}
                onClick={handleToggleCostCenterModal}
              >
                {selectedCostCenter ? (
                  cutString(selectedCostCenter.label, 30)
                ) : (
                  <FormattedMessage id="BrandedFares.ModalTable.SelectBrand" />
                )}

                <Img
                  src="/static/images/ico-arrow-down.svg"
                  alt="down arrow icon"
                  className={classnames(
                    {
                      "select-arrow-rotate-up": costCenterModalOpen.open,
                    },

                    "passenger-options-button-text-icon-indentation"
                  )}
                />
              </button>

              {costCenterModalOpen.open && (
                <ReusableSelect
                  className="passengers-options-dialog__reusable-select"
                  optionsArray={allowedCostCenters}
                  onChange={({ label, value }) => {
                    dispatch(changeCostCenter(value));
                    setCostCenterModalOpen((prevState) => ({
                      ...prevState,
                      open: false,
                      chosen: label,
                    }));
                  }}
                />
              )}
            </div>
          </div>
          <div className="passengers-options-dialog__row">
            <div>
              <FormattedMessage id="General.travelReason" />
            </div>

            <div>
              <SimpleSelect
                right
                id="cbt-travel-reason"
                placeholder={intl.formatMessage({
                  id: "BrandedFares.ModalTable.SelectBrand",
                })}
                className="passengers-options-dialog__select"
                options={travelReasons}
                onChange={({ value }) => {
                  dispatch(changeTravelReason(value));
                }}
                isSearchable={false}
                styles={cbtSelectStyles}
                value={travelReasons.find(
                  ({ value }) => value === travelReason
                )}
                components={{
                  SingleValue(props) {
                    return (
                      <div
                        style={
                          props.data.label ? { textTransform: "uppercase" } : {}
                        }
                        className="passengers-options-dialog__selected-value"
                      >
                        {cutString(props.data.label, 28)}
                      </div>
                    );
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}
      <div className="action-buttons">
        {needMoreAdultsError && (
          <div className="error-message-container">
            <FormattedMessage
              id={needMoreAdultsError}
              values={{ maxAge: Number(childSettings?.MaxAge) + 1 }}
            />
          </div>
        )}
        {maxTravelersError && (
          <div className="error-message-container">
            <FormattedMessage
              id={maxTravelersError}
              values={{
                count: Number(
                  hotelSearchSettings?.TotalGuestCount?.Max ??
                    hotelSearchSettings?.RoomOccupancy?.Max ??
                    9
                ),
              }}
            />
          </div>
        )}
        {maxRoomOccupancyError && (
          <div className="error-message-container">
            <FormattedMessage
              id={maxRoomOccupancyError}
              values={{
                count: Number(hotelSearchSettings?.RoomOccupancy?.Max ?? 9),
                roomNumber:
                  maxRoomOccupancyErrorRoomIndex !== null
                    ? maxRoomOccupancyErrorRoomIndex + 1
                    : 1,
              }}
            />
          </div>
        )}
        <div
          className={classnames("buttons-section-wrapper", {
            "done-only": isCbtEnabled,
          })}
        >
          {!isCbtEnabled && renderRoomManagementButtons()}
          <div className="buttons-container done-button">
            <button
              type="button"
              id="done-room-button"
              title={intl.formatMessage({ id: "PropertiesSelect.done" })}
              disabled={!!needMoreAdultsError}
              onClick={toggleShowProperties}
              className="button button-secondary-color"
            >
              <FormattedMessage id="PropertiesSelect.done" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const getAgeWord = (intl: IntlShape, age: number): string => {
  return intl.formatMessage({ id: "SearchForm.age" }, { count: age });
};

export const generateChildAgeOptions = (
  intl: IntlShape
): Array<{ label: string; value: string }> => {
  const minAge = 0;
  const maxAge = 18;
  const length = maxAge - minAge + 1;

  return Array.from({ length }, (_, i) => ({
    label: getAgeWord(intl, i + minAge),
    value: (i + minAge).toString(),
  }));
};

export default HotelPassengerSelect;
